import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Bell, KeyRound, Mail, Plus, Trash2, UserCog } from 'lucide-react';

import {
  ALERT_TYPE_OPTIONS,
  alertTypeOption,
  createSubscription,
  deleteSubscription,
  listAlertEvents,
  listSubscriptions,
  type AlertSubscription,
  type AlertType,
  type SubscriptionFilter,
} from '@/api/alerts.api';
import {
  CAPABILITY_OPTIONS,
  grantCapability,
  listCapabilities,
  revokeCapability,
  type Capability,
  type MembershipCapability,
} from '@/api/capabilities.api';
import { listEmployees, type EmployeeListItem } from '@/api/employees.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';

/**
 * Alerts settings — Phase 14, completed in post-audit Tier 2.
 *
 * Three tabs, each with its own access rule (mirroring the server's
 * gates exactly):
 *   - Capabilities  — superadmin only (grant/revoke is superadmin
 *     server-side)
 *   - Subscriptions — superadmin OR `manage_alerts` grant
 *   - Dispatches    — superadmin OR `view_alert_dispatches` grant
 *
 * The page itself admits anyone who can see at least one tab, so a
 * department_lead granted `manage_alerts` really does get the access
 * the Capabilities tab promises.
 */

type Tab = 'capabilities' | 'subscriptions' | 'dispatches';

const TAB_LABELS: Record<Tab, { label: string; icon: typeof Bell }> = {
  capabilities: { label: 'Capabilities', icon: KeyRound },
  subscriptions: { label: 'Subscriptions', icon: Bell },
  dispatches: { label: 'Dispatches', icon: Mail },
};

export function AlertsSettingsPage() {
  const { state: authState } = useAuth();

  const access = useMemo(() => {
    if (authState.status !== 'authenticated') {
      return { capabilities: false, subscriptions: false, dispatches: false };
    }
    const me = authState.me;
    return {
      capabilities: isSuperadmin(me),
      subscriptions: hasCapability(me, 'manage_alerts'),
      dispatches: hasCapability(me, 'view_alert_dispatches'),
    };
  }, [authState]);

  const visibleTabs = useMemo(
    () => (Object.keys(TAB_LABELS) as Tab[]).filter((key) => access[key]),
    [access],
  );

  // Default to the first tab this member can actually see.
  const [tab, setTab] = useState<Tab | null>(null);
  const activeTab = tab && access[tab] ? tab : (visibleTabs[0] ?? null);

  if (visibleTabs.length === 0) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to dashboard
        </Link>
        <Card className="flex items-start gap-3 p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-shark">Access required</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Alerts settings are visible to visionary and integrator roles, and to members granted the
              manage-alerts or view-alert-dispatches capability. Ask a superadmin for a grant if you need
              access.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-3.5" />
          Back to dashboard
        </Link>
        <h1 className="typo-h1 mt-1">Alerts</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Configure who can manage alerts, who receives them, and review what was sent. Overtime and ShipEdge
          sync failures are the first alert types — more land as the framework grows.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-kw-border">
        {visibleTabs.map((key) => {
          const { label, icon: Icon } = TAB_LABELS[key];
          const active = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                setTab(key);
              }}
              className={`relative flex items-center gap-2 px-3 py-2 text-[13px] ${
                active ? 'text-kw-shark' : 'text-kw-muted hover:text-kw-shark'
              }`}
            >
              <Icon className="size-4" />
              {label}
              {active ? <span className="absolute inset-x-0 -bottom-px h-0.5 bg-kw-cerulean" /> : null}
            </button>
          );
        })}
      </div>

      {activeTab === 'capabilities' ? (
        <CapabilitiesTab />
      ) : activeTab === 'subscriptions' ? (
        <SubscriptionsTab />
      ) : activeTab === 'dispatches' ? (
        <DispatchesTab />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Capabilities tab
// ─────────────────────────────────────────────────────────────────────────

function CapabilitiesTab() {
  const queryClient = useQueryClient();
  const [grantOpen, setGrantOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<MembershipCapability | null>(null);

  const capabilitiesQuery = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => listCapabilities(),
  });

  // Capabilities reference memberships, not employees, so we load the
  // employees list (which carries `membershipId`) and join client-side
  // for display. Acceptable at the team's scale (≤ 100 employees per
  // org); a dedicated /api/memberships endpoint becomes worthwhile
  // around 1000+.
  const employeesQuery = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => listEmployees({ status: 'all', limit: 500 }),
  });

  const membershipIdToEmployee = useMemo(() => {
    const map = new Map<string, EmployeeListItem>();
    for (const emp of employeesQuery.data?.data ?? []) {
      if (emp.membershipId) map.set(emp.membershipId, emp);
    }
    return map;
  }, [employeesQuery.data]);

  // Employees we can grant TO: anyone with a Membership. Filtering in the
  // grant modal — capabilities only make sense for portal-active people.
  const grantableEmployees = useMemo(() => {
    return (employeesQuery.data?.data ?? []).filter((e) => e.membershipId);
  }, [employeesQuery.data]);

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['capabilities'] }).then(() => undefined);
  }

  const isLoading = capabilitiesQuery.isLoading || employeesQuery.isLoading;
  const error = capabilitiesQuery.error ?? employeesQuery.error;
  const rows = capabilitiesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] text-kw-muted">
          Grant non-superadmins fine-grained access to alerting features. Visionary and Integrator roles
          implicitly hold every capability and don&apos;t need explicit grants.
        </p>
        <Button
          onClick={() => {
            setGrantOpen(true);
          }}
        >
          <Plus className="size-4" />
          Grant capability
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load capabilities</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <UserCog className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No capabilities granted yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Grant a capability when you want a department lead or other team member to manage alerts on
              their own. Superadmins (visionary, integrator) already have everything.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {rows.map((row) => {
              const employee = membershipIdToEmployee.get(row.membershipId);
              const capability = CAPABILITY_OPTIONS.find((c) => c.value === row.capability);
              return (
                <li key={row.id} className="flex items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <p className="text-[14px] font-medium text-kw-shark">
                        {employee ? `${employee.firstName} ${employee.lastName}` : row.membershipId}
                      </p>
                      <span className="rounded-kw-sm bg-kw-bg-blue px-1.5 py-0.5 font-mono text-[11px] text-kw-cerulean">
                        {row.capability}
                      </span>
                    </div>
                    {capability ? (
                      <p className="mt-0.5 text-[12px] text-kw-muted">{capability.description}</p>
                    ) : null}
                    <p className="mt-0.5 text-[11px] text-kw-muted">
                      Granted {new Date(row.grantedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label={`Revoke ${row.capability}`}
                    onClick={() => {
                      setPendingRevoke(row);
                    }}
                    className="shrink-0 rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <GrantCapabilityModal
        open={grantOpen}
        onClose={() => {
          setGrantOpen(false);
        }}
        employees={grantableEmployees}
        existingGrants={rows}
        onGranted={() => {
          void invalidate();
        }}
      />

      <RevokeCapabilityModal
        row={pendingRevoke}
        employee={pendingRevoke ? (membershipIdToEmployee.get(pendingRevoke.membershipId) ?? null) : null}
        onClose={() => {
          setPendingRevoke(null);
        }}
        onRevoked={() => {
          void invalidate();
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Grant modal
// ─────────────────────────────────────────────────────────────────────────

function GrantCapabilityModal({
  open,
  onClose,
  employees,
  existingGrants,
  onGranted,
}: {
  open: boolean;
  onClose: () => void;
  employees: EmployeeListItem[];
  existingGrants: MembershipCapability[];
  onGranted: () => void;
}) {
  const [membershipId, setMembershipId] = useState('');
  const [capability, setCapability] = useState<Capability>('manage_alerts');

  const mutation = useMutation({
    mutationFn: () => grantCapability({ membershipId, capability }),
    onSuccess: () => {
      onGranted();
      onClose();
      setMembershipId('');
      setCapability('manage_alerts');
    },
  });

  // Hide options that are already granted to the chosen membership.
  // Lets the admin not waste a click on a no-op upsert.
  const alreadyGrantedFor = useMemo(() => {
    return new Set(existingGrants.filter((g) => g.membershipId === membershipId).map((g) => g.capability));
  }, [existingGrants, membershipId]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!membershipId) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Grant capability" size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Recipient</span>
          <select
            required
            value={membershipId}
            onChange={(e) => {
              setMembershipId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">Pick a teammate…</option>
            {employees.map((emp) => (
              <option key={emp.membershipId} value={emp.membershipId ?? ''}>
                {emp.firstName} {emp.lastName} ({emp.email})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Capability</span>
          <select
            required
            value={capability}
            onChange={(e) => {
              setCapability(e.target.value as Capability);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            {CAPABILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={alreadyGrantedFor.has(opt.value)}>
                {opt.label}
                {alreadyGrantedFor.has(opt.value) ? ' (already granted)' : ''}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-kw-muted">
            {CAPABILITY_OPTIONS.find((c) => c.value === capability)?.description}
          </span>
        </label>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not grant'}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!membershipId}>
            Grant
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Revoke modal
// ─────────────────────────────────────────────────────────────────────────

function RevokeCapabilityModal({
  row,
  employee,
  onClose,
  onRevoked,
}: {
  row: MembershipCapability | null;
  employee: EmployeeListItem | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => {
      if (!row) return Promise.resolve();
      return revokeCapability(row.id);
    },
    onSuccess: () => {
      onRevoked();
      onClose();
    },
  });

  return (
    <Modal open={Boolean(row)} onClose={onClose} title="Revoke capability" size="sm">
      {row ? (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-shark">
            Revoke{' '}
            <code className="rounded bg-kw-bg px-1 py-0.5 font-mono text-[11px]">{row.capability}</code> from{' '}
            <strong>{employee ? `${employee.firstName} ${employee.lastName}` : row.membershipId}</strong>?
          </p>
          <p className="text-[12px] text-kw-muted">
            They&apos;ll lose access to the gated UI immediately. You can re-grant any time.
          </p>
          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {mutation.error instanceof Error ? mutation.error.message : 'Could not revoke'}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={mutation.isPending}
              onClick={() => {
                mutation.mutate();
              }}
            >
              Revoke
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Subscriptions tab
// ─────────────────────────────────────────────────────────────────────────

function SubscriptionsTab() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AlertSubscription | null>(null);

  const subscriptionsQuery = useQuery({
    queryKey: ['alert-subscriptions'],
    queryFn: () => listSubscriptions(false),
  });

  const employeesQuery = useQuery({
    queryKey: ['employees', 'all'],
    queryFn: () => listEmployees({ status: 'all', limit: 500 }),
  });

  // Same client-side membership↔employee join as the Capabilities tab.
  const membershipIdToEmployee = useMemo(() => {
    const map = new Map<string, EmployeeListItem>();
    for (const emp of employeesQuery.data?.data ?? []) {
      if (emp.membershipId) map.set(emp.membershipId, emp);
    }
    return map;
  }, [employeesQuery.data]);

  const employeesWithMembership = useMemo(() => {
    return (employeesQuery.data?.data ?? []).filter((e) => e.membershipId);
  }, [employeesQuery.data]);

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['alert-subscriptions'] }).then(() => undefined);
  }

  const isLoading = subscriptionsQuery.isLoading || employeesQuery.isLoading;
  const error = subscriptionsQuery.error ?? employeesQuery.error;
  const rows = subscriptionsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] text-kw-muted">
          Decide who gets which alerts and on which scope — overtime, ShipEdge sync failures, and overdue
          training today; new alert types drop in without rework.
        </p>
        <Button
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <Plus className="size-4" />
          New subscription
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load subscriptions</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Bell className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No subscriptions yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Create your first one — pick a recipient and scope (everyone, or specific employees).
            </p>
          </div>
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            New subscription
          </Button>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {rows.map((row) => {
              const recipient = membershipIdToEmployee.get(row.recipientMembershipId);
              // Render-safe: never dereference an unknown alert type
              // (server may know types this build doesn't).
              const typeOption = alertTypeOption(row.alertType);
              return (
                <li key={row.id} className="flex items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <p className="text-[14px] font-medium text-kw-shark">
                        {recipient
                          ? `${recipient.firstName} ${recipient.lastName}`
                          : row.recipientMembershipId}
                      </p>
                      <span className="rounded-kw-sm bg-kw-bg-blue px-1.5 py-0.5 text-[11px] text-kw-cerulean">
                        {typeOption.label}
                      </span>
                      <span className="text-[11px] text-kw-muted">via {row.channel}</span>
                    </div>
                    <p className="mt-0.5 text-[12px] text-kw-muted">
                      {describeFilter(row.filterJson, membershipIdToEmployee)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-kw-muted">
                      Created {new Date(row.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Delete subscription"
                    onClick={() => {
                      setPendingDelete(row);
                    }}
                    className="shrink-0 rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <CreateSubscriptionModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        employees={employeesWithMembership}
        onCreated={() => {
          void invalidate();
        }}
      />

      <DeleteSubscriptionModal
        row={pendingDelete}
        recipient={
          pendingDelete ? (membershipIdToEmployee.get(pendingDelete.recipientMembershipId) ?? null) : null
        }
        onClose={() => {
          setPendingDelete(null);
        }}
        onDeleted={() => {
          void invalidate();
        }}
      />
    </div>
  );
}

function describeFilter(
  filter: SubscriptionFilter,
  membershipIdToEmployee: Map<string, EmployeeListItem>,
): string {
  if (filter.kind === 'all') return 'Scope: every alert of this type.';
  if (filter.employeeMembershipIds.length === 0) return 'Scope: no employees selected.';
  const names = filter.employeeMembershipIds
    .map((id) => {
      const emp = membershipIdToEmployee.get(id);
      return emp ? `${emp.firstName} ${emp.lastName}` : id;
    })
    .join(', ');
  return `Scope: alerts about ${names}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Create subscription modal
// ─────────────────────────────────────────────────────────────────────────

function CreateSubscriptionModal({
  open,
  onClose,
  employees,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  employees: EmployeeListItem[];
  onCreated: () => void;
}) {
  const [alertType, setAlertType] = useState<AlertType>('overtime');
  const [recipientMembershipId, setRecipientMembershipId] = useState('');
  const [scope, setScope] = useState<'all' | 'employee_in'>('all');
  const [selectedEmployeeMembershipIds, setSelectedEmployeeMembershipIds] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: () => {
      const filter: SubscriptionFilter =
        scope === 'all'
          ? { kind: 'all' }
          : { kind: 'employee_in', employeeMembershipIds: selectedEmployeeMembershipIds };
      return createSubscription({ alertType, recipientMembershipId, filter });
    },
    onSuccess: () => {
      onCreated();
      onClose();
      // Reset to defaults so the next "New" doesn't carry stale state.
      setAlertType('overtime');
      setRecipientMembershipId('');
      setScope('all');
      setSelectedEmployeeMembershipIds([]);
    },
  });

  function toggleEmployee(membershipId: string): void {
    setSelectedEmployeeMembershipIds((prev) =>
      prev.includes(membershipId) ? prev.filter((id) => id !== membershipId) : [...prev, membershipId],
    );
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!recipientMembershipId) return;
    if (scope === 'employee_in' && selectedEmployeeMembershipIds.length === 0) return;
    mutation.mutate();
  }

  const submitDisabled =
    !recipientMembershipId || (scope === 'employee_in' && selectedEmployeeMembershipIds.length === 0);

  return (
    <Modal open={open} onClose={onClose} title="New subscription" size="md">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Alert type</span>
          <select
            required
            value={alertType}
            onChange={(e) => {
              setAlertType(e.target.value as AlertType);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            {ALERT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-kw-muted">{alertTypeOption(alertType).description}</span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Recipient</span>
          <select
            required
            value={recipientMembershipId}
            onChange={(e) => {
              setRecipientMembershipId(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">Pick a teammate to notify…</option>
            {employees.map((emp) => (
              <option key={emp.membershipId} value={emp.membershipId ?? ''}>
                {emp.firstName} {emp.lastName} ({emp.email})
              </option>
            ))}
          </select>
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-[12px] font-medium text-kw-muted">Scope</legend>
          <label className="flex items-start gap-2 text-[13px] text-kw-shark">
            <input
              type="radio"
              name="scope"
              checked={scope === 'all'}
              onChange={() => {
                setScope('all');
              }}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Every alert</span>
              <span className="ml-1 text-kw-muted">
                — receive every event of this type, for any employee.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-[13px] text-kw-shark">
            <input
              type="radio"
              name="scope"
              checked={scope === 'employee_in'}
              onChange={() => {
                setScope('employee_in');
              }}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Specific employees</span>
              <span className="ml-1 text-kw-muted">
                — only receive alerts about the people checked below (e.g. for a manager who covers a subset
                of the warehouse).
              </span>
            </span>
          </label>
        </fieldset>

        {scope === 'employee_in' ? (
          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-kw-muted">
              Employees in scope ({selectedEmployeeMembershipIds.length} selected)
            </span>
            <div className="max-h-48 overflow-y-auto rounded-kw-md border border-kw-border p-2">
              {employees.length === 0 ? (
                <p className="p-2 text-[12px] text-kw-muted">No employees with a portal account yet.</p>
              ) : (
                <ul className="space-y-1">
                  {employees.map((emp) => (
                    <li key={emp.membershipId}>
                      <label className="flex items-center gap-2 rounded px-1 py-0.5 text-[13px] text-kw-shark hover:bg-kw-bg">
                        <input
                          type="checkbox"
                          checked={selectedEmployeeMembershipIds.includes(emp.membershipId ?? '')}
                          onChange={() => {
                            if (emp.membershipId) toggleEmployee(emp.membershipId);
                          }}
                        />
                        <span>
                          {emp.firstName} {emp.lastName}
                          <span className="ml-1 text-[11px] text-kw-muted">{emp.email}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not create'}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={submitDisabled}>
            Create subscription
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Delete subscription modal
// ─────────────────────────────────────────────────────────────────────────

function DeleteSubscriptionModal({
  row,
  recipient,
  onClose,
  onDeleted,
}: {
  row: AlertSubscription | null;
  recipient: EmployeeListItem | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => {
      if (!row) return Promise.resolve();
      return deleteSubscription(row.id);
    },
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <Modal open={Boolean(row)} onClose={onClose} title="Delete subscription" size="sm">
      {row ? (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-shark">
            Stop sending alerts to{' '}
            <strong>
              {recipient ? `${recipient.firstName} ${recipient.lastName}` : row.recipientMembershipId}
            </strong>
            ?
          </p>
          <p className="text-[12px] text-kw-muted">
            We&apos;ll suspend the subscription, not hard-delete it — past dispatches keep their history. You
            can recreate the same subscription later.
          </p>
          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {mutation.error instanceof Error ? mutation.error.message : 'Could not delete'}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={mutation.isPending}
              onClick={() => {
                mutation.mutate();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Dispatches tab (post-audit Tier 2 — replaces the Coming-soon placeholder)
// ─────────────────────────────────────────────────────────────────────────

function DispatchesTab() {
  const [typeFilter, setTypeFilter] = useState<AlertType | ''>('');
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  const eventsQuery = useQuery({
    queryKey: ['alert-events', typeFilter],
    queryFn: () => listAlertEvents({ type: typeFilter === '' ? undefined : typeFilter, limit: 50 }),
    refetchInterval: 60_000, // soft refresh — catches cron-fired events
  });

  const events = eventsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <p className="text-[13px] text-kw-muted">
          Every alert event + who it was sent to, newest first. Click a row to see the email body that went
          out.
        </p>
        <label className="flex shrink-0 flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Type</span>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as AlertType | '');
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">All types</option>
            {ALERT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {eventsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : eventsQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load dispatches</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {eventsQuery.error instanceof Error ? eventsQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : events.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Mail className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No alert events yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Events appear here when the overtime evaluator or a ShipEdge sync failure fires. Create a
              subscription first so dispatches have somewhere to go.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {events.map((event) => {
              const expanded = expandedEventId === event.id;
              const typeOption = alertTypeOption(event.alertType);
              return (
                <li key={event.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedEventId(expanded ? null : event.id);
                    }}
                    aria-expanded={expanded}
                    className="flex w-full items-start gap-3 p-4 text-left hover:bg-kw-bg/40"
                  >
                    <SeverityBadge severity={event.severity} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-medium text-kw-shark">
                        {event.payload.subject}
                      </p>
                      <p className="mt-0.5 text-[12px] text-kw-muted">
                        {typeOption.label} · {new Date(event.occurredAt).toLocaleString()} ·{' '}
                        {event.dispatches.length} {event.dispatches.length === 1 ? 'recipient' : 'recipients'}
                        {event.dispatches.some((d) => d.status === 'failed') ? (
                          <span className="ml-1 text-kw-danger-text">— delivery failures</span>
                        ) : null}
                      </p>
                    </div>
                  </button>
                  {expanded ? (
                    <div className="space-y-3 border-t border-kw-border bg-kw-bg/30 p-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-kw-muted">Recipients</p>
                        {event.dispatches.length === 0 ? (
                          <p className="mt-1 text-[13px] text-kw-muted">
                            No subscriptions matched — the event was recorded but nobody was emailed.
                          </p>
                        ) : (
                          <ul className="mt-1 space-y-1">
                            {event.dispatches.map((dispatch) => (
                              <li key={dispatch.id} className="flex items-baseline gap-2 text-[13px]">
                                <DispatchStatusBadge status={dispatch.status} />
                                <span className="text-kw-shark">{dispatch.recipientName}</span>
                                <span className="text-kw-muted">{dispatch.recipientEmail}</span>
                                {dispatch.error ? (
                                  <span
                                    className="truncate text-[12px] text-kw-danger-text"
                                    title={dispatch.error}
                                  >
                                    {dispatch.error}
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-kw-muted">Email body</p>
                        <pre className="mt-1 whitespace-pre-wrap rounded-kw-md border border-kw-border bg-white p-3 font-sans text-[12px] text-kw-shark">
                          {event.payload.bodyText}
                        </pre>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: 'info' | 'trim' | 'breach' }) {
  const styles: Record<'info' | 'trim' | 'breach', string> = {
    info: 'bg-kw-bg-blue text-kw-cerulean',
    trim: 'bg-kw-warning-bg text-kw-warning-text',
    breach: 'bg-kw-danger-bg text-kw-danger-text',
  };
  return (
    <span
      className={`mt-0.5 inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[severity]}`}
    >
      {severity}
    </span>
  );
}

function DispatchStatusBadge({ status }: { status: 'pending' | 'sent' | 'failed' }) {
  const styles: Record<'pending' | 'sent' | 'failed', string> = {
    sent: 'bg-kw-emerald-light text-kw-tag-text',
    pending: 'bg-kw-bg-blue text-kw-cerulean',
    failed: 'bg-kw-danger-bg text-kw-danger-text',
  };
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles[status]}`}
    >
      {status}
    </span>
  );
}
